/*
 * Canavita cart drawer
 * ---------------------------------------------------------------------------
 * Adds on top of Pipeline's own cart drawer:
 *   1. Opens the drawer whenever something is added to the cart — from the
 *      theme, from PageFly pages, from quick-add and from apps (the last three
 *      are caught by watching network calls to /cart/add).
 *   2. Discount codes straight in the cart via /cart/update.js, behaving the
 *      same way checkout does.
 *   3. A server rendered order summary (?section_id=api-cart-summary) so all
 *      money formatting stays identical to the rest of the store.
 *   4. The delivery countdown and the in-drawer info popover.
 *
 * Everything is additive: Pipeline keeps owning quantity changes, line item
 * rendering and the open/close animation.
 */
(function () {
  'use strict';

  var DRAWER_SELECTOR = '[data-drawer="drawer-cart"]';
  var ADD_URL_PATTERN = /\/cart\/add(\.js)?(\?|$)/;

  var routes = (window.theme && window.theme.routes) || {};
  var rootUrl = routes.root_url || '/';
  if (rootUrl.charAt(rootUrl.length - 1) !== '/') rootUrl += '/';
  var cartUrl = routes.cart || '/cart';
  var cartAddUrl = routes.cart_add_url || rootUrl + 'cart/add';

  document.documentElement.classList.add('cnv-cart-enabled');

  function drawer() { return document.querySelector(DRAWER_SELECTOR); }
  function isOpen() {
    var d = drawer();
    return !!d && d.classList.contains('drawer--visible');
  }
  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* --- Opening ----------------------------------------------------------- */

  var lastOpen = 0;
  function openDrawer() {
    var d = drawer();
    if (!d) return false;
    if (Date.now() - lastOpen < 400) return true;
    lastOpen = Date.now();
    d.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
    return true;
  }

  /* --- Reacting to an add-to-cart ---------------------------------------- */

  var addLock = 0;
  function handleAdded() {
    if (Date.now() - addLock < 500) return;
    addLock = Date.now();

    window
      .fetch(cartUrl + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true })
        );
        if (drawer() && !isOpen()) {
          // Opening the drawer makes Pipeline re-render the line items itself.
          openDrawer();
        } else {
          document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        }
        refreshSummary();
        startCountdown();
      })
      .catch(function () {
        // Even if the refresh fails the customer should still see the drawer.
        if (drawer() && !isOpen()) openDrawer();
      });
  }

  // Pipeline fires this instead of showing its little "added" popdown.
  document.addEventListener('theme:cart:popdown', function () { handleAdded(); }, true);

  // Anything else on the page that posts to /cart/add — PageFly, the sticky
  // add-to-cart app, quick-add buttons, other apps.
  if (typeof window.fetch === 'function') {
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) { /* noop */ }
      var promise = nativeFetch.apply(this, arguments);
      if (url && ADD_URL_PATTERN.test(url)) {
        promise
          .then(function (response) { if (response && response.ok) handleAdded(); })
          .catch(function () { /* noop */ });
      }
      return promise;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try {
      this.__cnvIsCartAdd =
        String(method || '').toUpperCase() === 'POST' && ADD_URL_PATTERN.test(String(url || ''));
    } catch (e) {
      this.__cnvIsCartAdd = false;
    }
    return nativeOpen.apply(this, arguments);
  };

  var nativeSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__cnvIsCartAdd) {
      this.addEventListener('load', function () {
        if (this.status >= 200 && this.status < 300) handleAdded();
      });
    }
    return nativeSend.apply(this, arguments);
  };

  // Plain (non-ajax) add-to-cart forms would navigate away to /cart. Catch the
  // ones nobody else handled and turn them into an ajax add instead.
  document.addEventListener(
    'submit',
    function (event) {
      if (event.defaultPrevented) return;
      var form = event.target;
      if (!form || form.nodeName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (!/\/cart\/add/.test(action)) return;
      if (!drawer()) return;

      event.preventDefault();
      window
        .fetch(cartAddUrl + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: new FormData(form)
        })
        .then(function (response) {
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        })
        .then(function (result) {
          // A successful add is picked up by the fetch hook above.
          if (!result.ok) showCartError(result.data && (result.data.description || result.data.message));
        })
        .catch(function () {
          showCartError(null);
        });
    },
    false
  );

  // The "BESTIL 1 FLASKE" / "BESTIL 3 FLASKER" buttons in the AI product grid
  // add the item and then run window.location.href = '/cart', which throws the
  // customer onto the old cart page. Handle the click in the capture phase so
  // their own listener never sees it, and let the drawer open instead.
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;
      var btn = event.target.closest('[data-action="buy-one"],[data-action="buy-package"]');
      if (!btn || !drawer()) return;

      var id = btn.getAttribute('data-variant-id') || btn.getAttribute('data-package-id');
      if (!id) return;

      event.preventDefault();
      event.stopPropagation();
      if (btn.disabled) return;
      btn.disabled = true;

      window
        .fetch(cartAddUrl + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
        })
        .then(function (response) {
          if (response.ok) return null;
          return response.json().then(function (data) {
            showCartError(data && (data.description || data.message));
          });
        })
        .catch(function () { showCartError(null); })
        .then(function () { btn.disabled = false; });
    },
    true
  );

  function showCartError(message) {
    var box = document.querySelector('[data-form-errors]');
    if (!box) return;
    box.innerHTML = '<p>' + (message || 'Varen kunne ikke lægges i kurven. Prøv igen.') + '</p>';
    box.style.display = '';
    openDrawer();
  }

  /* --- Order summary ------------------------------------------------------ */

  var summaryTimer = null;
  function refreshSummary() {
    var host = document.querySelector('[data-cnv-summary]');
    if (!host) return;
    clearTimeout(summaryTimer);
    summaryTimer = setTimeout(function () {
      window
        .fetch(rootUrl + '?section_id=api-cart-summary', { credentials: 'same-origin' })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var holder = document.createElement('div');
          holder.innerHTML = html;
          var content = holder.querySelector('[data-api-content]');
          if (!content) return;
          host.innerHTML = content.innerHTML;
          syncFromSummary();
        })
        .catch(function () { /* keep whatever is on screen */ });
    }, 60);
  }

  function syncFromSummary() {
    var count = document.querySelector('[data-cnv-count-value]');
    var countTarget = document.querySelector('[data-cnv-count]');
    if (count && countTarget) countTarget.textContent = count.textContent.trim();
  }

  /* --- Discount codes ----------------------------------------------------- */
  /*
   * The codes live on the cart, not in the DOM — /cart.js is the only place
   * that reports a code Shopify is holding but not applying yet (empty cart,
   * minimum not reached, no qualifying product). Liquid only ever sees codes
   * that currently apply, so reading the state from the rendered markup made
   * pending codes vanish and get wiped on the next update.
   *
   * A code is therefore never removed automatically: it stays on the cart and
   * starts working by itself the moment a qualifying product is added — the
   * same behaviour as checkout.
   */

  var cartCodes = [];

  function renderCodes() {
    var host = document.querySelector('[data-cnv-codes]');
    if (!host) return;
    var html = '';
    cartCodes.forEach(function (entry) {
      var name = escapeHtml(entry.code);
      html +=
        '<span class="cnv-code' + (entry.applicable ? '' : ' cnv-code--waiting') + '">' +
        name +
        '<button type="button" class="cnv-code__remove" data-cnv-remove-discount="' + name +
        '" aria-label="Fjern rabatkoden ' + name + '">' +
        '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>' +
        '</button></span>';
    });
    host.innerHTML = html;
  }

  function syncCodes(cart) {
    if (!cart) return;
    cartCodes = (cart.discount_codes || []).filter(function (entry) {
      return entry && entry.code;
    });
    renderCodes();

    // A pending code that has started working should stop explaining itself.
    var msg = document.querySelector('[data-cnv-discount-msg]');
    if (msg && msg.classList.contains('is-info')) {
      var stillWaiting = cartCodes.some(function (entry) { return !entry.applicable; });
      if (!stillWaiting) discountMessage('');
    }
  }

  function codeList() {
    return cartCodes.map(function (entry) { return entry.code; });
  }

  function postDiscount(value) {
    return window
      .fetch(cartUrl + '/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: value })
      })
      .then(function (response) {
        return response
          .json()
          .catch(function () { return {}; })
          .then(function (data) { return { ok: response.ok, data: data }; });
      });
  }

  function afterDiscountChange(cart) {
    if (cart && cart.item_count !== undefined) {
      document.dispatchEvent(
        new CustomEvent('theme:cart:change', { detail: { cart: cart }, bubbles: true })
      );
    }
    // Discounts can change line prices, so re-render the items too.
    document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
    refreshSummary();
  }

  function discountMessage(text, isError) {
    var msg = document.querySelector('[data-cnv-discount-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.classList.toggle('is-error', !!text && !!isError);
    msg.classList.toggle('is-info', !!text && !isError);
  }

  function setDiscountBusy(busy) {
    var wrap = document.querySelector('[data-cnv-discount]');
    if (wrap) wrap.classList.toggle('is-busy', !!busy);
  }

  function applyDiscount(code) {
    code = (code || '').trim();
    if (!code) return;

    var next = codeList().filter(function (existing) {
      return existing.toLowerCase() !== code.toLowerCase();
    });
    next.push(code);

    setDiscountBusy(true);
    discountMessage('');

    postDiscount(next.join(','))
      .then(function (result) {
        if (!result.ok) {
          discountMessage(
            (result.data && (result.data.description || result.data.message)) ||
              'Rabatkoden kunne ikke anvendes.',
            true
          );
          return null;
        }

        var cart = result.data || {};
        var entry = (cart.discount_codes || []).filter(function (c) {
          return String(c.code).toLowerCase() === code.toLowerCase();
        })[0];

        var input = document.querySelector('[data-cnv-discount-input]');
        if (input) input.value = '';

        if (!entry) {
          discountMessage('Rabatkoden "' + code + '" kunne ikke anvendes.', true);
        } else if (entry.applicable) {
          discountMessage('');
        } else if (!cart.item_count) {
          discountMessage('Rabatkoden er gemt og bliver brugt, så snart du har varer i kurven.', false);
        } else {
          discountMessage(
            'Rabatkoden "' + code + '" gælder ikke for varerne i kurven — den bliver brugt automatisk, hvis du tilføjer en vare den dækker.',
            false
          );
        }

        syncCodes(cart);
        return cart;
      })
      .then(function (cart) { afterDiscountChange(cart); })
      .catch(function () {
        discountMessage('Der opstod en fejl. Prøv igen.', true);
      })
      .then(function () { setDiscountBusy(false); });
  }

  function removeDiscount(code) {
    var keep = codeList().filter(function (existing) {
      return String(existing).toLowerCase() !== String(code).toLowerCase();
    });
    setDiscountBusy(true);
    discountMessage('');
    postDiscount(keep.join(','))
      .then(function (result) {
        syncCodes(result.data);
        afterDiscountChange(result.data);
      })
      .catch(function () { /* noop */ })
      .then(function () { setDiscountBusy(false); });
  }

  // A link like ?discount=CODE (Shopify's own /discount/CODE links are applied
  // server side and simply show up in /cart.js).
  function discountFromUrl() {
    try {
      var params = new URLSearchParams(window.location.search);
      return (params.get('discount') || params.get('discount_code') || '').trim();
    } catch (e) {
      return '';
    }
  }

  /* --- Info popover ------------------------------------------------------- */

  function infoPanel() { return document.querySelector('[data-cnv-info]'); }

  function toggleInfo(open) {
    var panel = infoPanel();
    if (!panel) return;
    panel.hidden = !open;
    var trigger = document.querySelector('[data-cnv-info-open]');
    if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      var close = panel.querySelector('.cnv-info__close');
      if (close) close.focus();
    } else if (trigger) {
      trigger.focus();
    }
  }

  /* --- Shared click / key handling ---------------------------------------- */

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    if (event.target.closest('[data-cnv-info-open]')) {
      event.preventDefault();
      toggleInfo(true);
      return;
    }
    if (event.target.closest('[data-cnv-info-close]')) {
      event.preventDefault();
      toggleInfo(false);
      return;
    }

    var apply = event.target.closest('[data-cnv-discount-submit]');
    if (apply) {
      event.preventDefault();
      var input = document.querySelector('[data-cnv-discount-input]');
      applyDiscount(input ? input.value : '');
      return;
    }

    var remove = event.target.closest('[data-cnv-remove-discount]');
    if (remove) {
      event.preventDefault();
      removeDiscount(remove.getAttribute('data-cnv-remove-discount'));
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;
    if (!event.target || !event.target.closest) return;
    var input = event.target.closest('[data-cnv-discount-input]');
    if (!input) return;
    event.preventDefault();
    applyDiscount(input.value);
  });

  // Escape closes the popover first, and must not reach Pipeline's own handler
  // or the whole drawer would close with it.
  document.addEventListener(
    'keyup',
    function (event) {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      var panel = infoPanel();
      if (panel && !panel.hidden) {
        event.stopPropagation();
        toggleInfo(false);
      }
    },
    true
  );

  /* --- Delivery countdown ------------------------------------------------- */

  var countdownTimer = null;

  function copenhagenNow() {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Copenhagen',
      hour12: false,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).formatToParts(new Date());

    var out = {};
    parts.forEach(function (part) { out[part.type] = part.value; });

    var weekdays = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return {
      day: weekdays[out.weekday] !== undefined ? weekdays[out.weekday] : new Date().getDay(),
      hour: parseInt(out.hour, 10) % 24,
      minute: parseInt(out.minute, 10),
      second: parseInt(out.second, 10),
      stamp: '' + out.year + out.month + out.day
    };
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function startCountdown() {
    var el = document.querySelector('[data-cnv-countdown]');
    if (!el) return;

    var cutoff = parseInt(el.getAttribute('data-cutoff-hour'), 10);
    if (isNaN(cutoff)) cutoff = 15;
    var minutes = parseInt(el.getAttribute('data-minutes'), 10);
    if (isNaN(minutes) || minutes < 1) minutes = 10;
    var lastDay = parseInt(el.getAttribute('data-last-day'), 10);
    if (isNaN(lastDay)) lastDay = 5;

    var template = el.getAttribute('data-text') || '';
    var expiredText = el.getAttribute('data-text-expired') || '';
    var textEl = el.querySelector('[data-cnv-countdown-text]');

    clearInterval(countdownTimer);

    function visibleNow() {
      var t = copenhagenNow();
      return t.day >= 1 && t.day <= lastDay && t.hour < cutoff;
    }

    if (!visibleNow()) {
      el.hidden = true;
      return;
    }
    el.hidden = false;

    var storageKey = 'cnvCartCountdown:' + copenhagenNow().stamp;
    var deadline = 0;
    try {
      deadline = parseInt(window.sessionStorage.getItem(storageKey), 10) || 0;
    } catch (e) { deadline = 0; }

    if (!deadline || deadline > Date.now() + minutes * 60000) {
      deadline = Date.now() + minutes * 60000;
      try { window.sessionStorage.setItem(storageKey, String(deadline)); } catch (e) { /* noop */ }
    }

    var expired = null;

    function render() {
      if (!visibleNow()) {
        el.hidden = true;
        clearInterval(countdownTimer);
        return;
      }

      var left = Math.floor((deadline - Date.now()) / 1000);

      if (left > 0) {
        var stamp = pad(Math.floor(left / 60)) + ':' + pad(left % 60);
        var time = textEl && textEl.querySelector('.cnv-countdown__time');
        if (expired !== false || !time) {
          expired = false;
          if (textEl) {
            textEl.innerHTML = template.replace(
              '||timer||',
              '<span class="cnv-countdown__time">' + stamp + '</span>'
            );
          }
        } else {
          time.textContent = stamp;
        }
        el.classList.remove('is-expired');
      } else if (expired !== true) {
        expired = true;
        if (textEl) textEl.innerHTML = (expiredText || template).replace('||timer||', '');
        el.classList.add('is-expired');
      }
    }

    render();
    countdownTimer = setInterval(render, 1000);
  }

  /* --- Trustpilot --------------------------------------------------------- */

  var trustpilotDone = false;
  function initTrustpilot() {
    if (trustpilotDone) return;
    var wrap = document.querySelector('[data-cnv-trustpilot]');
    if (!wrap) return;
    var box = wrap.querySelector('.trustpilot-widget');
    if (!box || !window.Trustpilot || typeof window.Trustpilot.loadFromElement !== 'function') return;
    trustpilotDone = true;
    try { window.Trustpilot.loadFromElement(box, true); } catch (e) { return; }
    setTimeout(function () {
      if (box.querySelector('iframe')) wrap.classList.add('is-live');
    }, 1800);
  }

  /* --- Wiring ------------------------------------------------------------- */

  document.addEventListener('theme:cart:change', function (event) {
    if (event.detail && event.detail.cart) syncCodes(event.detail.cart);
    refreshSummary();
  });
  document.addEventListener('theme:cart:reload', function () { refreshSummary(); });

  function onDrawerOpen() {
    refreshSummary();
    startCountdown();
    initTrustpilot();
  }

  function bindDrawer() {
    var d = drawer();
    if (!d || d.__cnvBound) return;
    d.__cnvBound = true;
    d.addEventListener('theme:drawer:open', onDrawerOpen);
    d.addEventListener('theme:drawer:close', function () { toggleInfo(false); });
  }

  function initCodes() {
    window
      .fetch(cartUrl + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        syncCodes(cart);
        var urlCode = discountFromUrl();
        if (!urlCode) return;
        var alreadyOnCart = cartCodes.some(function (entry) {
          return entry.code.toLowerCase() === urlCode.toLowerCase();
        });
        if (!alreadyOnCart) applyDiscount(urlCode);
      })
      .catch(function () { /* noop */ });
  }

  function boot() {
    bindDrawer();
    startCountdown();
    syncFromSummary();
    initCodes();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function () {
    bindDrawer();
    startCountdown();
    renderCodes();
  });
})();
